import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../../hooks/useBoardChartData';
import { BaseChartProps } from '../../charts/types';
import { CHART_COLORS, getCurrencySymbol, formatCurrency } from '../../charts/chartTheme';
import { format, parseISO } from 'date-fns';

interface WaterfallItem {
    name: string;
    value: number;
    cumulative: number;
    isPositive: boolean;
    isTotal?: boolean;
}

export const CashFlowWaterfall: React.FC<BaseChartProps & { days?: Date[] }> = ({ days }) => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const waterfallData = useMemo((): WaterfallItem[] => {
        const txs = finance?.transactions || [];
        
        // Filter by days if provided
        let filteredTxs = txs;
        if (days && days.length > 0) {
            const daysSet = new Set(days.map(d => format(d, 'yyyy-MM-dd')));
            filteredTxs = txs.filter(tx => {
                if (!tx.date || tx.isActive === false) return false;
                try {
                    return daysSet.has(format(parseISO(tx.date), 'yyyy-MM-dd'));
                } catch(e) { return false; }
            });
        } else if (!finance?.breakdown) {
            return [];
        }

        const getCategoryTotals = (category: string) => {
            if (days && days.length > 0) {
                const items = filteredTxs.filter(item => (item.category || '').toLowerCase() === category.toLowerCase());
                const revenues = items.filter(i => i.type === 'REVENUE').reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
                const expenses = items.filter(i => i.type === 'EXPENSE').reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
                return { totalRevenues: Math.abs(revenues), totalExpenses: Math.abs(expenses) };
            } else {
                const items = (finance?.breakdown || []).filter(item => item.category === category);
                const revenues = items.filter(i => i.type === 'REVENUE').reduce((sum, i) => sum + (Number(i.value) || 0), 0);
                const expenses = items.filter(i => i.type === 'EXPENSE').reduce((sum, i) => sum + Math.abs(Number(i.value) || 0), 0);
                return { totalRevenues: revenues, totalExpenses: expenses };
            }
        };

        const productSale = getCategoryTotals('product_sale');
        const discount = getCategoryTotals('discount');
        const productReturn = getCategoryTotals('product_return');
        const partialReturn = getCategoryTotals('partial_return');
        const shippingCat = getCategoryTotals('shipping');
        const taxCat = getCategoryTotals('tax');
        
        const advertising = getCategoryTotals('advertising');
        const adSpend = getCategoryTotals('ad_spend');
        const transactionFee = getCategoryTotals('transaction_fee');
        const processingFee = getCategoryTotals('processing_fee');
        const platformFee = getCategoryTotals('platform_fee');
        const productCost = getCategoryTotals('product_cost');
        const cogsCat = getCategoryTotals('cogs');
        const operatingExpense = getCategoryTotals('operating_expense');
        const opexCat = getCategoryTotals('opex');

        const totalRevenue = (productSale.totalRevenues || 0) + (shippingCat.totalRevenues || 0) + (taxCat.totalRevenues || 0) - (discount.totalExpenses || 0) - (productReturn.totalExpenses || 0) - (partialReturn.totalExpenses || 0);
        const marketing = (advertising.totalExpenses || 0) + (adSpend.totalExpenses || 0);
        const fees = (transactionFee.totalExpenses || 0) + (processingFee.totalExpenses || 0) + (platformFee.totalExpenses || 0);
        const cogs = (productCost.totalExpenses || 0) + (cogsCat.totalExpenses || 0);
        const opex = (operatingExpense.totalExpenses || 0) + (opexCat.totalExpenses || 0);

        const items: WaterfallItem[] = [];
        let cumulative = 0;

        // Total Revenue (start)
        cumulative = totalRevenue;
        items.push({
            name: t('charts.components.totalRevenue'),
            value: totalRevenue,
            cumulative,
            isPositive: true,
            isTotal: true
        });

        // Marketing (negative branch)
        if (marketing > 0) {
            cumulative -= marketing;
            items.push({
                name: t('charts.components.marketing'),
                value: -marketing,
                cumulative,
                isPositive: false
            });
        }

        // COGS (negative branch)
        if (cogs > 0) {
            cumulative -= cogs;
            items.push({
                name: t('charts.components.cogs', 'COGS'),
                value: -cogs,
                cumulative,
                isPositive: false
            });
        }

        // Fees (negative branch)
        if (fees > 0) {
            cumulative -= fees;
            items.push({
                name: t('charts.components.fees', 'Fees'),
                value: -fees,
                cumulative,
                isPositive: false
            });
        }

        // OpEx (negative branch)
        if (opex > 0) {
            cumulative -= opex;
            items.push({
                name: t('charts.components.opex', 'OpEx'),
                value: -opex,
                cumulative,
                isPositive: false
            });
        }

        // Net Profit (End)
        items.push({
            name: t('charts.components.netProfit'),
            value: cumulative,
            cumulative,
            isPositive: cumulative >= 0,
            isTotal: true
        });

        return items;
    }, [finance, days, t]);

    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
    const currencySymbol = getCurrencySymbol(currencySettings?.default);
    const maxValue = Math.max(...waterfallData.map(d => Math.abs(d.cumulative)), 1);

    if (waterfallData.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-transparent">
                <div className="text-center p-6">
                    <p className="text-gray-500 dark:text-neutral-400 text-sm">{t('charts.components.noFinancialData')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-transparent flex flex-col pt-4">
            <div className="overflow-x-auto overflow-y-hidden px-4 custom-scrollbar">
                <div className="flex items-end h-[350px] min-w-max pb-12 relative pt-20">
                    {waterfallData.map((item, index) => {
                        const isFirst = index === 0;
                        const isLast = index === waterfallData.length - 1;
                        
                        // Height is based on absolute value
                        const barHeight = (Math.abs(item.value) / maxValue) * 200;
                        
                        // Position logic
                        let bottomPosition = 0;
                        if (item.isTotal || isFirst) {
                            bottomPosition = 0;
                        } else {
                            if (item.value > 0) {
                                bottomPosition = (Math.abs(item.cumulative - item.value) / maxValue) * 200;
                            } else {
                                bottomPosition = (Math.abs(item.cumulative) / maxValue) * 200;
                            }
                        }

                        // Determine the start position for the connector to the *next* block
                        let nextBottomLine = 0;
                        if (!isLast) {
                           nextBottomLine = (Math.abs(item.cumulative) / maxValue) * 200;
                        }

                        const isActive = activeIndex === index;
                        const isInactive = activeIndex !== null && activeIndex !== index;

                        return (
                            <div 
                                key={item.name} 
                                className={`relative flex flex-col items-center flex-shrink-0 transition-all duration-300 ${isInactive ? 'opacity-40 grayscale-[0.5]' : 'opacity-100'}`} 
                                style={{ width: '130px' }}
                                onMouseEnter={() => setActiveIndex(index)}
                                onMouseLeave={() => setActiveIndex(null)}
                            >
                                {/* Label Top */}
                                <div className={`absolute -top-10 w-full text-center transition-transform duration-300 ${isActive ? '-translate-y-1' : ''}`}>
                                    <span 
                                        className={`text-[11px] ${item.isTotal || isFirst ? 'font-black' : 'font-bold'}`}
                                        style={{
                                            color: item.isTotal || isFirst
                                                ? (item.isPositive ? CHART_COLORS.success : CHART_COLORS.danger)
                                                : (item.isPositive ? CHART_COLORS.success : CHART_COLORS.expense)
                                        }}
                                    >
                                        {item.value > 0 && !item.isTotal && !isFirst ? '+' : ''}{formatCurrency(item.value, currencySymbol)}
                                    </span>
                                </div>
                                
                                {/* Bar */}
                                <div className="w-full relative h-[200px]">
                                    <div 
                                        className={`absolute w-[calc(100%-12px)] left-[6px] transition-all duration-300 rounded-sm cursor-pointer ${isActive ? 'ring-2 ring-offset-2 ring-neutral-200 dark:ring-neutral-700' : ''} flex items-center justify-center overflow-hidden`}
                                        style={{
                                            bottom: `${bottomPosition}px`,
                                            height: `${Math.max(barHeight, 4)}px`,
                                            backgroundColor: item.isTotal
                                                ? (item.isPositive ? CHART_COLORS.primary : CHART_COLORS.danger)
                                                : (item.isPositive ? CHART_COLORS.revenue : CHART_COLORS.expense),
                                            boxShadow: item.isTotal ? '0 4px 6px -1px rgb(0 0 0 / 0.1)' : (isActive ? '0 10px 15px -3px rgb(0 0 0 / 0.1)' : 'none')
                                        }}
                                    >
                                        {barHeight > 40 && (
                                            <span className="text-[9px] font-black text-white/90 rotate-0">
                                                {formatCurrency(Math.abs(item.value), currencySymbol)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Tooltip Overlay */}
                                    {isActive && (
                                        <div className="absolute bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 bg-white dark:bg-neutral-800 p-3 rounded-xl shadow-xl border border-neutral-100 dark:border-neutral-700 z-[100] min-w-[160px] animate-in fade-in zoom-in-95 duration-200 pointer-events-none mb-10">
                                            <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">{item.name}</div>
                                            <div className="text-sm font-black text-neutral-900 dark:text-neutral-100 mb-2">
                                                {formatCurrency(item.value, currencySymbol)}
                                            </div>
                                            <div className="pt-2 border-t border-neutral-50 dark:border-neutral-700">
                                                <div className="flex justify-between items-center text-[10px]">
                                                    <span className="text-neutral-500">{t('charts.components.runningTotal', 'Running Total')}</span>
                                                    <span className="font-bold text-neutral-800 dark:text-neutral-200">{formatCurrency(item.cumulative, currencySymbol)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Connector Line to next bar */}
                                    {!isLast && (
                                        <>
                                            {/* Horizontal Branch */}
                                            <div 
                                                className={`absolute border-t-2 border-dashed transition-colors duration-300 ${isActive || (activeIndex === index + 1) ? 'border-neutral-400 dark:border-neutral-500' : 'border-neutral-300 dark:border-neutral-600'}`}
                                                style={{
                                                    bottom: `${nextBottomLine}px`,
                                                    left: 'calc(100% - 6px)',
                                                    width: '12px',
                                                    zIndex: 10
                                                }}
                                            />
                                            {/* Vertical connection for branching look */}
                                            {!waterfallData[index + 1].isTotal && (
                                                <div 
                                                    className="absolute border-l-2 border-dashed border-neutral-200 dark:border-neutral-700"
                                                    style={{
                                                        bottom: `${nextBottomLine}px`,
                                                        height: '100px', // Just a visual trace
                                                        left: 'calc(100% - 6px)',
                                                        zIndex: 5,
                                                        opacity: 0.3
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                                
                                {/* xAxis Label */}
                                <div className="w-full text-center mt-6 px-1 transition-colors duration-300 flex flex-col items-center">
                                    <span className={`text-[9px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-1 ${item.isTotal ? 'font-black' : 'font-bold'}`}>
                                        {item.name}
                                    </span>
                                    <span className={`text-[11px] font-black ${item.isTotal || isFirst ? (item.isPositive ? 'text-emerald-500' : 'text-red-500') : 'text-neutral-700 dark:text-neutral-300'}`}>
                                        {formatCurrency(item.cumulative, currencySymbol)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
